import React from 'react';
import clsx from 'clsx';

type ToggleSwitchOption<T> = {
  label: string;
  value: T;
};

type ToggleMode =
  | {
      mode: 'toggle';
      value: boolean;
      onChange: (next: boolean) => void;
      left: ToggleSwitchOption<boolean>;
      right: ToggleSwitchOption<boolean>;
    }
  | {
      mode?: 'select';
      value: string;
      onChange: (next: string) => void;
      left: ToggleSwitchOption<string>;
      right: ToggleSwitchOption<string>;
    };

type ToggleSwitchButtonProps = ToggleMode & {
  className?: string;
  disabled?: boolean;
  ariaLabel?: string;
  dataTestId?: string;
  title?: string;
};

const ToggleSwitchButton: React.FC<ToggleSwitchButtonProps> = (props) => {
  const {
    value,
    onChange,
    left,
    right,
    className,
    disabled = false,
    ariaLabel = 'Toggle',
    dataTestId,
    title,
  } = props;

  const isRight = value === right.value;

  const selectSide = (side: 'left' | 'right') => {
    if (disabled) return;
    const next = side === 'left' ? left.value : right.value;
    if (next === value) return;
    (onChange as (val: typeof next) => void)(next);
  };

  const handleSpanClick = (
    event: React.MouseEvent<HTMLSpanElement>,
    side: 'left' | 'right'
  ) => {
    event.stopPropagation();
    selectSide(side);
  };

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (event.detail !== 0) return;
    selectSide(isRight ? 'left' : 'right');
  };

  return (
    <div className="toggle_container">
      {title && <span className="toggle_title">{title}</span>}
      <button
        type="button"
        role="switch"
        aria-checked={isRight}
        aria-label={ariaLabel}
        disabled={disabled}
        data-test-id={dataTestId}
        className={clsx('toggle_button', className)}
        onClick={handleButtonClick}
      >
        <span
          className={clsx('toggle_option', !isRight && 'active')}
          onClick={(e) => handleSpanClick(e, 'left')}
        >
          {left.label}
        </span>
        <span
          className={clsx('toggle_option', isRight && 'active')}
          onClick={(e) => handleSpanClick(e, 'right')}
        >
          {right.label}
        </span>
      </button>
    </div>
  );
};

export default ToggleSwitchButton;
